/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import InputMask from 'react-input-mask';
import _omit from 'lodash/omit';
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import convertHtmlToDraft from 'html-to-draftjs';
import FilePondSingleUpload from 'uploader/FilePondSingleUpload';
import _isEmpty from 'lodash/isEmpty';
import '../../../../node_modules/react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { reactSelectCustomStyleCms } from 'helper';
import _ from 'lodash';
import ImagePlaceholder from 'assets/img/image-placeholder.jpg';
import Hospital from 'modules/civil-registry/components/Hospital';
import Citizenship from 'modules/civil-registry/components/Citizenship';
import DateInputPicker from '../components/DateInputPicker';
import Region from '../address/Region';
import Province from '../address/Province';
import Municipality from '../address/Municipality';
import Barangay from '../address/Barangay';

const parseNumber = (val) => {
  try {
    return parseFloat(val);
  } catch (err) {
    return NaN;
  }
};

const count = (s1, letter) => s1.split(letter).length - 1;

export default (WrappedComponent) =>
  function WithForm(props) {
    const [form, setForm] = useState({});

    const handleEditorStateChange = (key) => (editorState) => {
      setForm({ ...form, [key]: editorState });
    };

    const handleChangeValue = (key) => (value) => {
      setForm((prev) => ({ ...prev, [key]: value }));
    };

    const handleChangeInput =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.value });
      };

    const handleLowerCase =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.value });
      };

    const handleChangeCheck =
      (key) =>
      ({ target }) => {
        setForm({ ...form, [key]: target.checked });
      };

    const handleChangeInputNumber =
      (key) =>
      ({ target }) => {
        const svalue = target.value;
        const value = parseNumber(svalue);
        if (
          (Number.isNaN(value) && !_isEmpty(value)) ||
          count(`${svalue}`, '.') > 1
        )
          return;
        setForm({ ...form, [key]: svalue.replace(/[^0-9\.]/g, '') }); // eslint-disable-line
      };

    const renderLabel = (x, p) => (
      <label className="form-label d-flex align-items-center">
        <span>
          {x.label}{' '}
          {p.required && !x.isReadOnly && (
            <span className="text-danger">
              <i>Required</i>
            </span>
          )}
        </span>
        {x.note && (
          <small className="ml-auto mr-0 text-muted">
            <i>{x.note}</i>
          </small>
        )}
      </label>
    );

    const handleChangeSelect = (key) => (v) => {
      const value = _.get(v, 'fullInfo.attributes.code');
      setForm({
        ...form,
        [key]: value,
        ...(key === 'region_code' && {
          province_code: '',
          municipality_code: '',
          barangay_code: '',
        }),
        ...(key === 'province_code' && {
          municipality_code: '',
          barangay_code: '',
        }),
        ...(key === 'municipality_code' && {
          barangay_code: '',
        }),
      });
    };

    const setFormData = (payload) => {
      setForm({ ...form, ...payload });
    };

    const resetFormData = (defaultValue = {}) => {
      setForm(defaultValue);
    };

    const createRichTextEditor = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        <Editor
          editorState={form[data.key] || EditorState.createEmpty()}
          onEditorStateChange={handleEditorStateChange(data.key)}
          {...p}
        />
      </React.Fragment>
    );

    const htmlToDraft = (data) => {
      const { contentBlocks, entityMap } = convertHtmlToDraft(data || '');

      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap
      );
      const editorState = EditorState.createWithContent(contentState);

      return editorState;
    };

    const createInput = (data, p = {}, className = '') => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className={`${className} form-control`}
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createLowerCaseInput = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            onChange={handleLowerCase(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createUpperCaseInput = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control-uppercase"
            onChange={handleLowerCase(data.key)}
            value={form[data.key] || ''}
            autoFocus
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createCheckbox = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            type="checkbox"
            readOnly
            checked={form[data.key] || false}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            type="checkbox"
            onChange={handleChangeCheck(data.key)}
            checked={form[data.key] || false}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTextArea = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <p style={{ minHeight: 80 }}>{form[data.key]}</p>
        ) : (
          <textarea
            className="form-control"
            rows="3"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
            style={{ resize: 'none' }}
          />
        )}
      </React.Fragment>
    );

    const createInputNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <input
            className="form-control"
            type="number"
            onChange={handleChangeInputNumber(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputMask = (data, mask, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask={mask}
            maskChar=""
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputMobileNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999999999999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTelephoneNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999-999-9999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createTinNumber = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999-999-999-999"
            alwaysShowMask
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const inputPin = (data, p = {}) => (
      <React.Fragment>
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <InputMask
            mask="999"
            alwaysShowMask
            className="form-control text-center number-spacing"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {...p}
          />
        )}
      </React.Fragment>
    );

    const createInputGroupNumber = (
      data,
      p = {},
      inputPlaceholder = '\u20B1'
    ) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text font-weight-bold">
                {inputPlaceholder}
              </span>
            </div>
            <input
              readOnly
              value={form[data.key] || ''}
              className="form-control"
              {...p}
            />
          </div>
        ) : (
          <div className="input-group">
            <div className="input-group-prepend">
              <span className="input-group-text font-weight-bold">
                {inputPlaceholder}
              </span>
            </div>
            <InputMask
              mask="9999999999"
              maskChar=""
              placeholder="0.00"
              className="form-control"
              onChange={handleChangeInput(data.key)}
              value={form[data.key] || ''}
              {...p}
            />
          </div>
        )}
      </React.Fragment>
    );

    const createSelect = (data, options, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control"
            readOnly
            value={
              (options.find((x) => x.value === form[data.key] || '') || {})
                .label || ''
            }
            {...p}
          />
        ) : (
          <select
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {..._omit(p, ['isLoading'])}
          >
            <option value="">
              {p.isLoading ? 'Loading...' : p.placeholder || '- Select -'}
            </option>
            {options.map((item, i) => (
              <option
                key={`${item.value}-${i}`} // eslint-disable-line
                value={item.value}
                disabled={item.disabled}
              >
                {item.label}
              </option>
            ))}
          </select>
        )}
      </React.Fragment>
    );

    const renderAddressField = (type, data) => {
      switch (type) {
        case 'region':
          return (
            <Region
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              styles={reactSelectCustomStyleCms()}
            />
          );
        case 'province':
          return (
            <Province
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              regionCode={_.get(form, 'region_code') || ''}
              styles={reactSelectCustomStyleCms()}
              isDisabled={!_.get(form, 'region_code')}
            />
          );
        case 'municipality':
          return (
            <Municipality
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              regionCode={_.get(form, 'region_code') || ''}
              provinceCode={_.get(form, 'province_code') || ''}
              styles={reactSelectCustomStyleCms()}
              isDisabled={!_.get(form, 'province_code')}
            />
          );
        case 'place_region':
          return (
            <Region
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              styles={reactSelectCustomStyleCms()}
            />
          );
        case 'place_province':
          return (
            <Province
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              regionCode={_.get(form, 'place_of_birth_region_code') || ''}
              styles={reactSelectCustomStyleCms()}
              isDisabled={!_.get(form, 'place_of_birth_region_code')}
            />
          );
        case 'place_municipality':
          return (
            <Municipality
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              regionCode={_.get(form, 'place_of_birth_region_code') || ''}
              provinceCode={_.get(form, 'place_of_birth_province_code') || ''}
              styles={reactSelectCustomStyleCms()}
              isDisabled={!_.get(form, 'place_of_birth_province_code')}
            />
          );
        case 'barangay':
          return (
            <Barangay
              className="form-control"
              onChange={handleChangeSelect(data.key)}
              value={form[data.key] || ''}
              regionCode={_.get(form, 'region_code') || ''}
              provinceCode={_.get(form, 'province_code') || ''}
              municipalityCode={_.get(form, 'municipality_code') || ''}
              styles={reactSelectCustomStyleCms()}
              isDisabled={!_.get(form, 'municipality_code')}
            />
          );
        default:
          return null;
      }
    };

    const createSelectAddress = (type, data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          renderAddressField(type, data)
        )}
      </React.Fragment>
    );

    const createSelectCountry = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <Citizenship
            name={data.key}
            onChange={handleChangeSelect(data.key)}
            value={form[data.key] || ''}
            styles={reactSelectCustomStyleCms()}
          />
        )}
      </React.Fragment>
    );

    const createSelectHospital = (data, p = {}) => (
      <React.Fragment>
        <div className="d-flex align-items-center justify-content-between">
          {renderLabel(data, p)}
          <small
            aria-hidden="true"
            className="p-1 text-primary"
            style={{ cursor: 'pointer' }}
            onClick={() =>
              setForm((prev) => ({ ...prev, [data.key]: 'OTHER' }))
            }
          >
            Other
          </small>
        </div>
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <Hospital
            name={data.key}
            value={form[data.key] || ''}
            onChange={handleChangeSelect(data.key)}
          />
        )}
      </React.Fragment>
    );

    const createInputDate = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        {data.isReadOnly ? (
          <input
            className="form-control-plaintext"
            readOnly
            value={form[data.key] || ''}
            {...p}
          />
        ) : (
          <DateInputPicker
            className="form-control"
            onChange={handleChangeInput(data.key)}
            value={form[data.key] || ''}
            {..._omit(p, ['isLoading'])}
            // maxDate={new Date()}
          />
        )}
      </React.Fragment>
    );

    const createUploader = (data, obj) => (
      <React.Fragment>
        <div className="d-flex align-items-center justify-between">
          {renderLabel(data, obj)}
        </div>
        <img
          className="w-100 rounded"
          src={form[data.key] || ImagePlaceholder}
          alt="form[data.key]"
        />
        <FilePondSingleUpload
          value={form[data.key] || ''}
          onChange={handleChangeValue(data.key)}
          required={obj.required}
        />
        {/* <Uploader
          label={data.label}
          className="btn btn-block btn-primary cms-bg-color-primary cms-outline-primary "
          customLoading={<i className="fas fa-spinner fa-spin" />}
          icon={<i className="far fa-image" />}
          onChange={handleChangeValue(data.key)}
          {...p}
        /> */}
      </React.Fragment>
    );

    const createReadOnly = (data, p = {}) => (
      <React.Fragment>
        {renderLabel(data, p)}
        <p className="form-control-plaintext" {...p}>
          {form[data.key] || '-'}
        </p>
      </React.Fragment>
    );

    return (
      <WrappedComponent
        {...props}
        formData={form}
        setFormData={setFormData}
        resetFormData={resetFormData}
        createInput={createInput}
        createRichTextEditor={createRichTextEditor}
        createLowerCaseInput={createLowerCaseInput}
        createUpperCaseInput={createUpperCaseInput}
        createCheckbox={createCheckbox}
        createTextArea={createTextArea}
        createInputNumber={createInputNumber}
        createSelect={createSelect}
        createReadOnly={createReadOnly}
        createInputMask={createInputMask}
        createInputMobileNumber={createInputMobileNumber}
        createUploader={createUploader}
        createInputGroupNumber={createInputGroupNumber}
        createTelephoneNumber={createTelephoneNumber}
        createTinNumber={createTinNumber}
        createInputDate={createInputDate}
        createSelectAddress={createSelectAddress}
        createSelectCountry={createSelectCountry}
        createSelectHospital={createSelectHospital}
        inputPin={inputPin}
        draftToHtml={draftToHtml}
        htmlToDraft={htmlToDraft}
        convertToRaw={convertToRaw}
      />
    );
  };
